import React from "react";
import { Link } from "react-router-dom";
import { fmtDate, labelize } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { canGetConsentProof } from "@/components/leads/ConsentProofButton";
import { ProofPackButton } from "@/components/leads/ProofPackButton";
import { EmailPackButton } from "./EmailPackButton";

/** Bad-lead reports table with row-select checkboxes and bulk-close bar. */
export default function BadLeadsTable({
  items, total, selected, setSelected, canReview, onBulkClose,
}) {
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const { user, hasPerm } = useAuth();
  const canPick = canReview || canGetConsentProof(user, hasPerm);
  const packLeadIds = [...new Set(items.filter((r) => selected[r.id]).map((r) => r.lead_id))];
  const toggleAll = (e) => {
    const next = {};
    if (e.target.checked) items.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  return (
    <div className="brut-card mt-4 overflow-x-auto">
      <div className="flex items-center justify-between px-4 py-2 border-b border-black">
        <div className="label-mono">{items.length} shown · {total} total</div>
        {canPick && selectedIds.length > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-badlead-bar">
            <span className="text-[11px] uppercase tracking-widest">{selectedIds.length} selected</span>
            <ProofPackButton leadIds={packLeadIds} testId="badlead-proof-pack-btn" />
            <EmailPackButton reportIds={selectedIds} />
            {canReview && (
              <button onClick={onBulkClose} data-testid="bulk-close"
                className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                Bulk close
              </button>
            )}
          </div>
        )}
      </div>
      <table className="w-full text-sm font-mono min-w-[900px]">
        <thead>
          <tr className="border-b border-black text-[10px] uppercase tracking-widest">
            {canPick && (
              <th className="py-2 px-2 text-left">
                <input type="checkbox" data-testid="badlead-select-all"
                  onChange={toggleAll}
                  checked={selectedIds.length > 0 && selectedIds.length === items.length} />
              </th>
            )}
            <th className="py-2 px-4 text-left">Report ID</th>
            <th className="py-2 px-4 text-left">Lead</th>
            <th className="py-2 px-4 text-left">Reason</th>
            <th className="py-2 px-4 text-left">Resolution</th>
            <th className="py-2 px-4 text-left">Status</th>
            <th className="py-2 px-4 text-left">Reporter</th>
            <th className="py-2 px-4 text-left">Reported</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-b border-zinc-200 hover:bg-zinc-100" data-testid={`report-row-${r.id}`}>
              {canPick && (
                <td className="py-2 px-2">
                  <input type="checkbox" data-testid={`badlead-select-${r.id}`}
                    checked={!!selected[r.id]}
                    onChange={(e) => setSelected({ ...selected, [r.id]: e.target.checked })} />
                </td>
              )}
              <td className="py-2 px-4">
                <Link to={`/bad-leads/${r.id}`} className="hover:underline">{r.id.slice(0, 8)}</Link>
              </td>
              <td className="py-2 px-4">
                <Link to={`/leads/${r.lead_id}`} className="hover:underline">
                  {r.lead_name} · {r.lead_id.slice(0, 8)}
                </Link>
              </td>
              <td className="py-2 px-4">{labelize(r.reason)}</td>
              <td className="py-2 px-4">{labelize(r.requested_resolution)}</td>
              <td className="py-2 px-4"><StatusBadge status={r.status} /></td>
              <td className="py-2 px-4">{r.reporting_user_name || "—"} ({r.reporting_org_type})</td>
              <td className="py-2 px-4 text-[11px]">{fmtDate(r.created_at)}</td>
            </tr>
          ))}
          {!items.length && (
            <tr>
              <td colSpan={canReview ? 8 : 7} className="py-6 text-center label-mono">No reports</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
